import styles from "./results.module.css"
import { Professional, SearchResults } from "@/hooks/useSearch"
import { useRef, useEffect } from "react"
import { ProfessionalCard } from "./professional-card"
import EmptyStateIcon from "@/components/IconsTsx/EmptyStateIcon"
import { trackResultCardClicked } from "@/lib/mixpanelClient"

interface ResultsListProps {
  searchResults: SearchResults | undefined;
  selectedProfessional: Professional | null;
  onProfessionalSelect: (professional: Professional) => void;
  hasSearched?: boolean; // Nuevo prop para indicar si se ha realizado una búsqueda
}

export function ResultsList({
  searchResults,
  selectedProfessional,
  onProfessionalSelect,
  hasSearched = false
}: ResultsListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // #11 result_card_clicked — solo clicks reales en la card (el pin del mapa va
  // por su propio evento). posicion_en_lista es 1-based sobre la lista completa.
  const handleCardSelect = (professional: Professional) => {
    trackResultCardClicked({
      posicion: (professional.globalIndex ?? 0) + 1,
      prestadorId: professional.CODIGO,
      tipoPrestador: professional.RUBRO || '',
      pagina: searchResults?.currentPage ?? 1,
    });
    onProfessionalSelect(professional);
  };

  // Exponer el containerRef para uso externo
  useEffect(() => {
    if (containerRef.current) {
      // @ts-expect-error - Agregar el ref al elemento para acceso externo
      containerRef.current._scrollContainer = containerRef.current;
    }
  }, []);

  // Efecto para hacer scroll una sola vez cuando se selecciona un profesional
  useEffect(() => {
    if (selectedProfessional && containerRef.current) {
      // Buscar la card del profesional seleccionado usando el data attribute
      const selectedCard = containerRef.current.querySelector(`[data-professional-id="${selectedProfessional.CODIGO}-${selectedProfessional.NOMBRE}-${selectedProfessional.APELLIDO}"]`);
      
      if (selectedCard) {
        const cardRect = selectedCard.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const relativeTop = cardRect.top - containerRect.top + containerRef.current.scrollTop;

        // Hacer scroll para que la card aparezca en la parte superior del contenedor
        containerRef.current.scrollTo({
          top: relativeTop,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedProfessional]);

  // Estado inicial: no se ha hecho ninguna búsqueda
  if (!hasSearched) {
    return (
      <div className={styles.resultsListEmpty}>
        <div className={styles.emptyContainer}>
          <div className={styles.emptyIcon}>
            <EmptyStateIcon />
          </div>
          <div className={styles.emptyText}>
            Aquí verás los resultados de tu búsqueda.
          </div>
        </div>
      </div>
    )
  }

  // Estado de búsqueda sin resultados. Ya se intentó el fallback a nivel país
  // (ver SearchBar.handleSearch) y tampoco encontró nada: no hay más que
  // ofrecer acá, solo invitar a cambiar los filtros.
  if (!searchResults || searchResults.professionals.length === 0) {
    return (
      <div className={styles.resultsListEmpty}>
        <div className={styles.emptyContainer}>
          <div className={styles.emptyIcon}>
            <EmptyStateIcon />
          </div>
          <div className={styles.emptyText}>
            Sin resultados encontrados.
          </div>
          <div className={styles.emptySubtext}>
            Volvé a intentar cambiando tu búsqueda.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.resultsList}>
      <div className={styles.resultsContainer} ref={containerRef} data-scroll-container>
        {searchResults.professionals.map((professional: Professional, index: number) => {
          const isSelected = selectedProfessional?.CODIGO === professional.CODIGO &&
            selectedProfessional?.NOMBRE === professional.NOMBRE &&
            selectedProfessional?.APELLIDO === professional.APELLIDO;

          return (
            <ProfessionalCard
              key={`${professional.CODIGO}-${professional.MATRICULA}-${professional.NOMBRE}-${professional.APELLIDO}-${index}`}
              professional={professional}
              isSelected={isSelected}
              onSelect={handleCardSelect}
            />
          );
        })}
      </div>
    </div>
  )
} 